/**
 * `<caos-object-caret>` — the per-object-tab dropdown menu (a faithful port of Dragon OS's
 * `ObjectNavCaret`): a small caret beside a workspace object tab that opens, WITHOUT loading the
 * list first, the object's **Recently Viewed** records + its **List Views** (Salesforce-style).
 * (Catalog: Navigation › ObjectCaret — the per-tab dropdown of what you can do with that object.)
 *
 * This is the reusable POPOVER only — a CONTROLLED menu, opened programmatically by the shell at a
 * caret's live screen rect. The caret TRIGGER itself lives on each tab inside `<caos-workspace-tabs>`
 * (which emits `tab-caret` with the object + anchor rect); the shell fetches the object's list views
 * + recents, sets them here, and calls `openAt()`. One instance is owned by the shell and reused for
 * every tab — mirroring how `<caos-nav-gadget>` is owned once and driven by the shell.
 *
 * Panel: portaled to `document.body` (its own shadow host, `position: fixed`) so the tab row's
 * horizontal overflow / stacking context can never clip it — the same technique as `<caos-nav-gadget>`
 * and Dragon's UI-308 App-Launcher portal fix. ~230px wide (Dragon's `MENU_WIDTH`), token surface bg,
 * 1px border, radius, float shadow.
 *
 * Data in (PROPERTIES — arrays cannot ride attributes):
 *   `recents` — `{ id, label, href }[]`, the object's recently-viewed records (empty ⇒ the section
 *               is omitted, exactly as Dragon's caret hides it when the MRU is empty).
 *   `views`   — `{ id, label, href }[]`, the object's list views (the built-in "All …" + saved views).
 *
 * Events out (bubbling + composed — the host owns navigation):
 *   `caret-navigate` — detail `{ href }`, a record or list view was chosen.
 *   `caret-close`    — the menu dismissed (outside click / Escape) so the host can drop its state.
 */
import { CaosElement, RESET, canOpenFloatingPanel, define, makeSheet } from '../base-element.js';
import { panelFocus, type PanelFocus } from '../focus.js';
import { anchorPanel } from '../anchored-panel.js';
import { makeIcon } from './icons.js';

export interface CaosCaretItem {
  id: string;
  label: string;
  href: string;
}

const MENU_WIDTH = 230;

/**
 * The caret's screen rectangle. `top` and `right` let the menu open above the caret when there is no
 * room below it (CAOS-659); a caller that passes only `left` and `bottom` still gets a menu kept on
 * screen, opening from that corner.
 */
export interface CaosCaretAnchor {
  left: number;
  bottom: number;
  top?: number;
  right?: number;
}
/**
 * CAOS-602 — the two section glyphs. `list` was the vocabulary's own mark redrawn from different
 * primitives (lines rather than paths) for the same six strokes, which is the version of this
 * problem that a search for a path string never finds.
 */
const clockIcon = (): SVGSVGElement => makeIcon('clock');
const listIcon = (): SVGSVGElement => makeIcon('list');

/** Shadow-root stylesheet for the portaled panel (body-hosted, so it can't be clipped). */
const panelSheet = makeSheet(`
  .panel {
    position: fixed;
    /* A NAMED layer, not a number — see the header note on layering (CAOS-586). */
    z-index: var(--caos-layer-dropdown);
    min-width: ${MENU_WIDTH}px;
    max-height: 70vh;
    overflow-y: auto;
    background: var(--caos-color-surface);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius);
    box-shadow: var(--caos-shadow-float);
    padding: 4px;
    font-family: var(--caos-font);
  }
  .panel[hidden] { display: none; }

  /* Dragon caret sectionLabel — mono 9px .18em uppercase, muted, icon-led. */
  .section {
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: var(--caos-font-mono);
    font-size: 9px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--caos-color-text-muted);
    padding: 8px 12px 4px;
  }
  .section svg { width: 12px; height: 12px; flex-shrink: 0; }

  .row {
    display: block;
    width: 100%;
    text-align: left;
    padding: 7px 12px;
    background: transparent;
    border: none;
    cursor: pointer;
    color: var(--caos-color-text);
    font-family: inherit;
    font-size: 13px;
    border-radius: var(--caos-radius-sm);
  }
  .row:hover { background: var(--caos-color-surface-2); }
  .row:focus-visible { outline: none; background: var(--caos-color-surface-2); box-shadow: var(--caos-focus-ring); }
`);

export class CaosObjectCaret extends CaosElement {
  private _recents: CaosCaretItem[] = [];
  private _views: CaosCaretItem[] = [];
  private open = false;

  private portalHost: HTMLDivElement | null = null;
  /** CAOS-651 — focus into the open menu, kept there, and handed back on close. */
  private panelFocus: PanelFocus | null = null;
  private portalRoot: ShadowRoot | null = null;
  private panel!: HTMLElement;
  private anchor: CaosCaretAnchor | null = null;

  private readonly onDocPointer = (e: MouseEvent): void => {
    if (!this.open) return;
    const path = e.composedPath();
    if (this.portalHost && path.includes(this.portalHost)) return;
    this.hide();
  };
  private readonly onDocKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.hide(true);
    else this.panelFocus?.trap(e);
  };
  private readonly reposition = (): void => {
    if (!this.open || !this.anchor) return;
    const { left, bottom, top = bottom, right = left } = this.anchor;
    anchorPanel(this.panel, { left, top, right, bottom }, { side: 'bottom', align: 'start', gap: 6 });
  };

  constructor() {
    // The host itself renders nothing — only the portaled panel is visible.
    super(makeSheet(`:host { display: none; }`));
  }

  connectedCallback(): void {
    this.ensurePortal();
  }

  disconnectedCallback(): void {
    document.removeEventListener('mousedown', this.onDocPointer);
    document.removeEventListener('keydown', this.onDocKey);
    window.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition);
    this.portalHost?.remove();
    this.portalHost = null;
    this.portalRoot = null;
    this.panelFocus = null;
  }

  /** The object's recently-viewed records (data in — a property, not an attribute). */
  get recents(): CaosCaretItem[] {
    return this._recents;
  }
  set recents(list: CaosCaretItem[]) {
    this._recents = Array.isArray(list) ? list : [];
    if (this.open) this.renderPanel();
  }

  /** The object's list views (data in — a property, not an attribute). */
  get views(): CaosCaretItem[] {
    return this._views;
  }
  set views(list: CaosCaretItem[]) {
    this._views = Array.isArray(list) ? list : [];
    if (this.open) this.renderPanel();
  }

  /** Open the menu anchored under a caret at its live screen rect. */
  openAt(anchor: CaosCaretAnchor): void {
    // `openAt()` is a PUBLIC entry point a screen calls directly, so a stale/detached instance
    // reaching it is genuinely reachable, not theoretical. Same rule as `caos-overflow-menu`'s
    // open path — a control that is not on the document must not get a panel on `document.body`
    // (CAOS-632).
    if (!canOpenFloatingPanel(this)) return;
    this.ensurePortal();
    this.anchor = anchor;
    this.open = true;
    this.panel.hidden = false;
    this.renderPanel();
    this.reposition();
    document.addEventListener('mousedown', this.onDocPointer);
    document.addEventListener('keydown', this.onDocKey);
    window.addEventListener('scroll', this.reposition, true);
    window.addEventListener('resize', this.reposition);
    // CAOS-651 — the workspace opens this on a tab's behalf, so the opener is whatever held focus
    // when it did: the caret button on that tab. Opened again while it is still open, focus is
    // already inside, so the same controller keeps that first caret as the one to return to.
    this.panelFocus ??= panelFocus(this.panel);
    this.panelFocus.open();
  }

  /**
   * Close the menu (idempotent). Emits `caret-close` so the host can drop its state. `restoreFocus`
   * returns focus to the control that opened it — Escape and a choice do; an outside click does not.
   */
  hide(restoreFocus = false): void {
    if (!this.open) return;
    this.open = false;
    if (this.panel) this.panel.hidden = true;
    document.removeEventListener('mousedown', this.onDocPointer);
    document.removeEventListener('keydown', this.onDocKey);
    window.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition);
    this.panelFocus?.close(restoreFocus);
    this.panelFocus = null;
    this.emit('caret-close');
  }

  private ensurePortal(): void {
    if (this.portalHost) return;
    this.portalHost = document.createElement('div');
    this.portalHost.className = 'caos-object-caret-portal';
    this.portalRoot = this.portalHost.attachShadow({ mode: 'open' });
    // RESET first, exactly as the base class does for the component's own root: this root is
    // outside that machinery, so without this line the shared hiding guard would not reach
    // anything inside the panel. See base-element.ts.
    this.portalRoot.adoptedStyleSheets = [RESET, panelSheet];

    this.panel = document.createElement('div');
    this.panel.className = 'panel';
    this.panel.setAttribute('role', 'menu');
    this.panel.hidden = true;
    this.portalRoot.append(this.panel);
    document.body.append(this.portalHost);
  }

  private emit(type: string, detail?: unknown): void {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true, detail }));
  }

  private addSection(label: string, icon: SVGSVGElement): void {
    const sec = document.createElement('div');
    sec.className = 'section';
    sec.append(icon);
    sec.append(document.createTextNode(label));
    this.panel.append(sec);
  }

  private addRow(item: CaosCaretItem): void {
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'row';
    row.setAttribute('role', 'menuitem');
    row.textContent = item.label;
    row.addEventListener('click', () => {
      this.emit('caret-navigate', { href: item.href });
      this.hide(true);
    });
    this.panel.append(row);
  }

  private renderPanel(): void {
    if (!this.portalRoot) return;
    this.panel.replaceChildren();
    // Recently Viewed — only when the MRU has entries (Dragon parity: the section is omitted otherwise).
    if (this._recents.length > 0) {
      this.addSection('Recently Viewed', clockIcon());
      for (const r of this._recents) this.addRow(r);
    }
    // List Views — always present.
    this.addSection('List Views', listIcon());
    for (const v of this._views) this.addRow(v);
  }
}

define('caos-object-caret', CaosObjectCaret);

declare global {
  interface HTMLElementTagNameMap {
    'caos-object-caret': CaosObjectCaret;
  }
}
